import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Learn.css';
import Flashcard from './Flashcard';
import QuizQuestion from './QuizQuestion';
import CircleTile from './CircleTile';
import { fetchLearnData, handleAnswer, moveToNextCard, startLearningSession } from './FirebaseUtils';
import { calculateStudiedCountToday, calculateTotalStudiedCount, calculateSessionCountToday, calculateTotalSessionCount, calculateThemeScore, selectWeightedItems } from './utils';
import { themes, tileConfigs, circlePositions } from './constants';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { normalizeTheme } from './utils';
import { defaultLearnScore, defaultStats, defaultUserData } from './defaults';

// Function to remove accents from a string
const removeAccents = (str) => {
  return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
};

// Levenshtein distance function for fuzzy matching
const levenshteinDistance = (a, b) => {
  const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));
  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;
  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  return matrix[b.length][a.length];
};

const Learn = ({ user: initialUser }) => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [displayedTheme, setDisplayedTheme] = useState("Divers");
  const [learnMode, setLearnMode] = useState('flashcards');
  const [currentSession, setCurrentSession] = useState({
    items: [],
    currentIndex: 0,
    currentCard: null,
    isCardLocked: false,
    answers: [],
    feedback: null,
  });

  const [userData, setUserData] = useState(null);
  const [learnData, setLearnData] = useState(null);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [themePositions, setThemePositions] = useState([...circlePositions]);
  const [resetSubThemeTrigger, setResetSubThemeTrigger] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [user, setUser] = useState(initialUser);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const loadData = async () => {
        try {
          // Check cache
          const cachedData = localStorage.getItem(`learnData_${user.uid}`);
          if (cachedData) {
            const { userData: cachedUserData, learnData: cachedLearnData } = JSON.parse(cachedData);
            const hasValidLearnScore = cachedUserData.learnScore && themes.every(theme => cachedUserData.learnScore[normalizeTheme(theme)] !== undefined);
            const hasValidStats = cachedUserData.stats && typeof cachedUserData.stats.flashcardsStudied === 'number';
            if (
              cachedUserData.sessionHistory &&
              cachedUserData.sessionHistory.length > 0 &&
              hasValidLearnScore &&
              hasValidStats
            ) {
              console.log('Utilisation de learnData depuis localStorage');
              setUserData({
                ...defaultUserData,
                ...cachedUserData,
                learnScore: { ...defaultLearnScore, ...cachedUserData.learnScore },
                stats: { ...defaultStats, ...cachedUserData.stats },
              });
              setLearnData(cachedLearnData);
              setIsDataLoaded(true);
              return;
            } else {
              console.warn('Données en cache invalides ou incomplètes, récupération depuis Firestore');
            }
          }

          // Fetch from Firestore
          await fetchLearnData(user, setUserData, setLearnData);
          setIsDataLoaded(true);
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
          setUserData(defaultUserData);
          setLearnData({});
          setIsDataLoaded(true);
        }
      };
      loadData();
    }
  }, [user]);

  const handleThemeClick = (theme) => {
    if (theme.endsWith('-start')) {
      const baseTheme = theme.split('-')[0];
      setDisplayedTheme(baseTheme);
      startLearningSession(baseTheme, setSelectedTheme, setCurrentSession, setSessionCompleted, setSessionStartTime, selectWeightedItems, learnData);
      setResetSubThemeTrigger((prev) => prev + 1);
    } else {
      const currentCenterIndex = themes.indexOf(displayedTheme);
      const clickedIndex = themes.indexOf(theme);
      if (!themes.includes(theme)) {
        setDisplayedTheme(theme);
        startLearningSession(theme, setSelectedTheme, setCurrentSession, setSessionCompleted, setSessionStartTime, selectWeightedItems, learnData);
        setResetSubThemeTrigger((prev) => prev + 1);
      } else if (currentCenterIndex !== clickedIndex) {
        const newPositions = [...themePositions];
        [newPositions[currentCenterIndex], newPositions[clickedIndex]] = [newPositions[clickedIndex], newPositions[currentCenterIndex]];
        setThemePositions(newPositions);
        setDisplayedTheme(theme);
        setResetSubThemeTrigger((prev) => prev + 1);
      }
    }
  };

  const handleBackToDashboard = () => {
    setCurrentSession({ items: [], currentIndex: 0, currentCard: null, isCardLocked: false, answers: [], feedback: null });
    setSessionCompleted(false);
    setSelectedTheme(null);
    setDisplayedTheme("Divers");
    setThemePositions([...circlePositions]);
    setResetSubThemeTrigger(0);
    setSessionStartTime(null);
  };

  const handleLearnModeChange = (mode) => {
    setLearnMode(mode);
  };

  // Render loading or auth error states
  if (!user) {
    return (
      <div className="learn-container" ref={containerRef}>
        <div className="learn-logo">
          <Link to="/">
            <img src="/dwag_bleu.png" alt="Logo Dwag" />
            <span className="learn-title"><span>D</span><span>W</span><span>A</span><span>G</span></span>
          </Link>
        </div>
        <div className="auth-error">
          <p>Veuillez vous connecter pour accéder au mode Apprentissage.</p>
          <Link to="/login">Se connecter</Link>
        </div>
      </div>
    );
  }

  if (!isDataLoaded || !userData || !learnData) {
    return (
      <div className="learn-container" ref={containerRef}>
        <div className="learn-logo">
          <Link to="/">
            <img src="/dwag_bleu.png" alt="Logo Dwag" />
            <span className="learn-title"><span>D</span><span>W</span><span>A</span><span>G</span></span>
          </Link>
        </div>
        <div className="loading">
          <span className="loading-title">
              <span>D</span>
              <span className="rotating">W</span>
              <span>A</span>
              <span>G</span>
            </span>
        </div>
      </div>
    );
  }

  return (
    <div className="learn-container" ref={containerRef}>
      <div className="learn-logo">
        <Link to="/">
          <img src="/dwag_bleu.png" alt="Logo Dwag" />
          <span className="learn-title"><span>D</span><span>W</span><span>A</span><span>G</span></span>
        </Link>
      </div>

      {!selectedTheme || (!currentSession.currentCard && !sessionCompleted) ? (
        <div className="dashboard">
          <div className="circle-grid">
            {themes.map((theme, index) => {
              const studiedToday = calculateStudiedCountToday(theme, learnData);
              const totalStudied = calculateTotalStudiedCount(theme, learnData);
              const sessionToday = calculateSessionCountToday(theme, userData);
              const totalSession = calculateTotalSessionCount(theme, userData);
              const score = calculateThemeScore(theme, learnData, userData.learnScore);
              return (
                <CircleTile
                  key={theme}
                  theme={theme}
                  config={tileConfigs[theme]}
                  onClick={handleThemeClick}
                  isActive={displayedTheme === theme}
                  isExpanded={displayedTheme === theme}
                  progress={score}
                  studiedCountToday={studiedToday}
                  totalStudiedCount={totalStudied}
                  sessionCountToday={sessionToday}
                  totalSessionCount={totalSession}
                  position={themePositions[index]}
                  resetSubTheme={resetSubThemeTrigger}
                />
              );
            })}
          </div>
          <div className="stats-corner">
            <p>Cartes étudiées: {userData.stats?.flashcardsStudied ?? 0}</p>
            <p>Streak: {userData.stats?.currentStreak ?? 0} jours</p>
          </div>
        </div>
      ) : (
        <div className="theme-content">
          {!sessionCompleted && (
            <div className="theme-header">
              <button onClick={handleBackToDashboard} className="back-to-system-button">Choisir un autre thème</button>
              <h1>{selectedTheme}</h1>
              <div className="mode-selector">
                <div className="switch-label">
                  <button className={`mode-btn ${learnMode === 'flashcards' ? 'active' : ''}`} onClick={() => handleLearnModeChange('flashcards')}>
                    Flashcards
                  </button>
                  <button className={`mode-btn ${learnMode === 'qcm' ? 'active' : ''}`} onClick={() => handleLearnModeChange('qcm')}>
                    QCM
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentSession.currentCard ? (
            <div className="content-area">
              <div className="learn-progress-container">
                <div className="progress-indicator">Carte {currentSession.currentIndex + 1}/{currentSession.items.length}</div>
                {currentSession.isCardLocked && (
                  <button onClick={() => moveToNextCard(currentSession, setCurrentSession, setSessionCompleted, userData, setUserData, user, selectedTheme, sessionStartTime, learnData, setLearnData)} className="next-question-button">
                    Question Suivante
                  </button>
                )}
              </div>
              {learnMode === 'flashcards' ? (
                <Flashcard
                  key={currentSession.items[currentSession.currentIndex]}
                  data={currentSession.currentCard}
                  isLocked={currentSession.isCardLocked}
                  onAnswer={(userAnswer, cardData) => {
                    // Vérifier si userAnswer est une chaîne, sinon utiliser une chaîne vide par défaut
                    const safeUserAnswer = typeof userAnswer === 'string' ? userAnswer : '';
                    const normalizedUserAnswer = removeAccents(safeUserAnswer.trim().toLowerCase());
                    const normalizedCorrectAnswer = removeAccents(cardData.answers[cardData.correctAnswer].trim().toLowerCase());
                    const maxEditDistance = 2;
                    const distance = levenshteinDistance(normalizedUserAnswer, normalizedCorrectAnswer);
                    const maxLength = Math.max(normalizedUserAnswer.length, normalizedCorrectAnswer.length);
                    const similarityThreshold = 0.8;
                    const isCorrect = distance <= maxEditDistance && (1 - distance / maxLength) >= similarityThreshold;
                    handleAnswer(isCorrect, currentSession, setCurrentSession, user, selectedTheme, learnData, setLearnData, userData, setUserData);
                  }}
                  feedback={currentSession.feedback}
                  image={currentSession.currentCard.image || null}
                  sound={currentSession.currentCard.sound || null}
                />
              ) : (
                <QuizQuestion
                  key={currentSession.items[currentSession.currentIndex]}
                  question={currentSession.currentCard.question}
                  answers={currentSession.currentCard.answers}
                  correctAnswer={currentSession.currentCard.correctAnswer}
                  onAnswerClick={(index) => {
                    const isCorrect = index === currentSession.currentCard.correctAnswer;
                    handleAnswer(isCorrect, currentSession, setCurrentSession, user, selectedTheme, learnData, setLearnData, userData, setUserData);
                  }}
                  isLocked={currentSession.isCardLocked}
                  feedback={currentSession.feedback}
                  image={currentSession.currentCard.image || null}
                  sound={currentSession.currentCard.sound || null}
                />
              )}
            </div>
          ) : (
            <div className="content-area">
              <div className="session-complete">
                <h2>Félicitations !</h2>
                <p>Vous avez terminé une session d'apprentissage.</p>
                <p>Flashcards étudiées aujourd’hui: {calculateStudiedCountToday(selectedTheme, learnData)}</p>
                <p>Flashcards étudiées (total): {calculateTotalStudiedCount(selectedTheme, learnData)}</p>
                <p>Sessions terminées aujourd’hui: {calculateSessionCountToday(selectedTheme, userData)}</p>
                <p>Sessions terminées (total): {calculateTotalSessionCount(selectedTheme, userData)}</p>
                <p>Score pour ce thème: {calculateThemeScore(selectedTheme, learnData, userData.learnScore).toFixed(2)}%</p>
                <p>Temps passé aujourd’hui: {userData.activity?.daily?.[new Date().toISOString().split('T')[0]]?.timeSpent || 0} minutes</p>
                <div className="button-group">
                  <button onClick={() => startLearningSession(selectedTheme, setSelectedTheme, userData, setCurrentSession, setSessionCompleted, setSessionStartTime, selectWeightedItems, learnData)} className="continue-button">
                    Continuer avec {selectedTheme}
                  </button>
                  <button onClick={handleBackToDashboard} className="back-to-system-button-end-session">Choisir un autre thème</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Learn;